import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { PeekResultsPanel } from './PeekResultsPanel';
import type { PeekResultsState } from './PeekResultsPanel';
import type { PeekAnchor } from '../hooks/usePeekResults';
import type { SqlMonacoHandle } from '../../common/utils/sqlMonacoTypes';

interface PeekResultsOverlayProps {
  editorRef: React.RefObject<SqlMonacoHandle | null>;
  state: PeekResultsState | null;
  /** Line the panel is anchored to — captured when Peek was clicked. */
  anchor: PeekAnchor | null;
  onClose: () => void;
  onOpenInResults?: (query: string) => void;
}

/** Pixel gap between the bottom of the selection and the top of the panel. */
const PANEL_TOP_GAP = 6;
/** Approximate panel width used when falling back to a right-edge clamp. */
const PANEL_MIN_WIDTH = 320;
/** Minimum pixel distance from the left edge of the editor container. */
const PANEL_LEFT_MARGIN = 8;
/**
 * Pixel margin from the top/bottom of the *window viewport* that the panel
 * must keep when we clamp (breathing room so the panel isn't flush against
 * the OS chrome / window edge).
 */
const VIEWPORT_MARGIN = 4;
/**
 * Fallback panel height used on the very first render, before ResizeObserver
 * has measured the DOM element.
 */
const FALLBACK_PANEL_HEIGHT = 340;

/**
 * Renders the Peek Results panel as a **fixed-positioned popup** rendered into
 * `document.body` via `createPortal`. Lives outside the editor's
 * `overflow-hidden` wrapper, so the panel can extend over the resizable
 * divider and the results grid below the editor when the anchor is near the
 * editor's bottom edge — and over the toolbar / Tabs above it when the anchor
 * is near the top. Clamping is against the **window viewport**, not the
 * editor's own viewport, so the panel only stops at the OS window edges.
 *
 * Position is recomputed on Monaco scroll, Monaco layout change, AND the
 * window's `resize` / `scroll` events so the panel sticks to the anchor line
 * across every layout change.
 *
 * Height-aware flip: the overlay measures its actual rendered height via
 * `ResizeObserver` and prefers below-anchor placement when there's room,
 * falls back to above-anchor when there isn't, and clamps inside the window
 * if neither side has full room — the panel's body is internally scrollable
 * (max-h on the table container) so content stays reachable.
 */
export function PeekResultsOverlay({
  editorRef,
  state,
  anchor,
  onClose,
  onOpenInResults,
}: PeekResultsOverlayProps) {
  const [position, setPosition] = useState<{ top: number; left: number; maxWidth: number } | null>(null);
  const [panelHeight, setPanelHeight] = useState<number>(FALLBACK_PANEL_HEIGHT);
  // Once the user drags the panel by its header, we stop following the
  // editor anchor and pin the panel to wherever they put it. The user
  // position is cleared when the panel closes or re-opens for a new peek.
  const [userPosition, setUserPosition] = useState<{ top: number; left: number } | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);

  const recompute = useCallback(() => {
    const editor = editorRef.current;
    if (!editor || !anchor) {
      setPosition(null);
      return;
    }
    const editorRelative = editor.getPixelPositionForPosition(anchor.lineNumber, anchor.column);
    if (!editorRelative) {
      // Anchor line is not currently rendered (scrolled way out of view).
      // Keep the panel hidden rather than pinning it to a stale position.
      setPosition(null);
      return;
    }
    const editorRect = editor.getEditorRect();
    if (!editorRect) {
      setPosition(null);
      return;
    }

    // Convert editor-relative pixel position → viewport (fixed) coordinates.
    const anchorTopViewport = editorRect.top + editorRelative.top;
    const anchorBottomViewport = anchorTopViewport + editorRelative.height;
    const anchorLeftViewport = editorRect.left + editorRelative.left;

    // We also need to know the editor's vertical extents inside the window —
    // but only as a lower bound for "where the line currently is". The panel
    // is now allowed to extend past the editor's own bottom (over the divider
    // and results grid) and past its own top (over the toolbar). The only
    // limit is the window viewport.
    const winHeight = typeof window !== 'undefined' ? window.innerHeight : 0;
    const winWidth = typeof window !== 'undefined' ? window.innerWidth : 0;

    // Default placement: directly below the anchor line.
    const belowTop = anchorBottomViewport + PANEL_TOP_GAP;
    // Flip placement: directly above the anchor line.
    const aboveTop = anchorTopViewport - PANEL_TOP_GAP - panelHeight;

    let finalTop: number;
    if (winHeight === 0) {
      finalTop = Math.max(VIEWPORT_MARGIN, belowTop);
    } else {
      const spaceBelow = winHeight - belowTop - VIEWPORT_MARGIN;
      const spaceAbove = anchorTopViewport - PANEL_TOP_GAP - VIEWPORT_MARGIN;
      const fitsBelow = panelHeight <= spaceBelow;
      const fitsAbove = panelHeight <= spaceAbove;

      if (fitsBelow) {
        finalTop = belowTop;
      } else if (fitsAbove) {
        finalTop = Math.max(VIEWPORT_MARGIN, aboveTop);
      } else {
        // Neither side has full room. Pick the side with more space and
        // clamp so the panel stays inside the window — the panel's body is
        // internally scrollable, so the content stays reachable.
        if (spaceBelow >= spaceAbove) {
          finalTop = Math.max(
            VIEWPORT_MARGIN,
            Math.min(belowTop, winHeight - panelHeight - VIEWPORT_MARGIN),
          );
        } else {
          finalTop = VIEWPORT_MARGIN;
        }
      }
    }

    // Constrain the panel to never extend past the right edge of the window.
    // Left clamps to a small margin off the editor's left edge; the panel
    // tries to start under the anchor's column, but no further left than
    // `editorRect.left + PANEL_LEFT_MARGIN`.
    const leftCandidate = Math.max(editorRect.left + PANEL_LEFT_MARGIN, anchorLeftViewport);
    const maxLeft = winWidth > 0 ? winWidth - PANEL_MIN_WIDTH - VIEWPORT_MARGIN : leftCandidate;
    const finalLeft = Math.max(VIEWPORT_MARGIN, Math.min(leftCandidate, Math.max(VIEWPORT_MARGIN, maxLeft)));
    const maxWidth = winWidth > 0
      ? Math.max(PANEL_MIN_WIDTH, winWidth - finalLeft - VIEWPORT_MARGIN)
      : PANEL_MIN_WIDTH;

    setPosition({ top: finalTop, left: finalLeft, maxWidth });
  }, [editorRef, anchor, panelHeight]);

  // Recompute whenever the panel becomes visible, the anchor changes, or the
  // measured panel height changes (panelHeight is a dep of recompute).
  useEffect(() => {
    if (state === null) {
      setPosition(null);
      return;
    }
    recompute();
  }, [state, recompute]);

  // Re-anchor on Monaco scroll / layout AND window resize / scroll, since
  // the panel is now in the window's coordinate space.
  useEffect(() => {
    if (state === null) return;
    const editor = editorRef.current;
    if (!editor) return;
    const offMonacoScroll = editor.onDidScrollChange(() => recompute());
    const offMonacoLayout = editor.onDidLayoutChange(() => recompute());
    const handleWindow = () => recompute();
    window.addEventListener('resize', handleWindow);
    // `scroll` with capture catches scrolls in any ancestor (e.g. results
    // grid scrolling shouldn't move the peek, but a parent panel/dialog
    // resize might shift the editor's bounding rect).
    window.addEventListener('scroll', handleWindow, true);
    return () => {
      offMonacoScroll();
      offMonacoLayout();
      window.removeEventListener('resize', handleWindow);
      window.removeEventListener('scroll', handleWindow, true);
    };
  }, [state, editorRef, recompute]);

  // Measure the panel's actual rendered height so the clamping logic above
  // can decide between below/above placement and accurately keep the panel
  // inside the window.
  useEffect(() => {
    if (state === null) return;
    const node = overlayRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return;

    const initialHeight = node.offsetHeight;
    if (initialHeight > 0 && initialHeight !== panelHeight) {
      setPanelHeight(initialHeight);
    }

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const h = entry.contentRect.height;
        if (h > 0) setPanelHeight((prev) => (Math.abs(prev - h) > 0.5 ? h : prev));
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
    // Intentionally re-run when state flips null → non-null so the observer
    // re-attaches after the overlay unmounts and remounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  // Reset the user-set position whenever the panel closes (state goes null)
  // or a brand-new peek opens. We do NOT reset on every state transition
  // (loading → success) — the panel stays put while data loads.
  useEffect(() => {
    if (state === null) {
      setUserPosition(null);
    }
  }, [state]);
  // Re-anchoring should also kick in if the user opens a new peek with a
  // different anchor line — the new peek replaces the old one and we want
  // it to start at the freshly-computed auto-position.
  useEffect(() => {
    setUserPosition(null);
  }, [anchor?.lineNumber, anchor?.column]);

  // Drag-by-header. Started by `onPointerDown` on the panel's header (wired
  // through `PeekResultsPanel.onHeaderPointerDown`). Skips drag when the
  // pointer started on a button inside the header (Close, Open in results)
  // so those clicks still register as button clicks instead of accidental
  // 0-pixel drags.
  const handleHeaderPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return; // only primary button
    const target = e.target;
    if (target instanceof Element && target.closest('button')) return;

    const overlay = overlayRef.current;
    if (!overlay) return;
    const startRect = overlay.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const baseTop = startRect.top;
    const baseLeft = startRect.left;
    const panelW = startRect.width;

    e.preventDefault();
    // Capture the pointer on the header so we keep getting move events even
    // if the cursor strays off the panel during the drag.
    try {
      (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    } catch {
      // setPointerCapture can throw in iframes / specific browser modes —
      // best-effort; the document-level listeners below still work.
    }

    const onMove = (ev: PointerEvent) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      const winW = window.innerWidth;
      const winH = window.innerHeight;
      // Clamp so the panel always stays at least partially on-screen — we
      // require a small visible strip on each side so the user can grab
      // the header back and drag it home if they end up at an extreme edge.
      const minLeft = VIEWPORT_MARGIN - panelW + 80;
      const maxLeft = winW - VIEWPORT_MARGIN - 80;
      const minTop = VIEWPORT_MARGIN;
      const maxTop = winH - VIEWPORT_MARGIN - 24; // keep header in-view
      const nextLeft = Math.max(minLeft, Math.min(maxLeft, baseLeft + dx));
      const nextTop = Math.max(minTop, Math.min(maxTop, baseTop + dy));
      setUserPosition({ top: nextTop, left: nextLeft });
    };
    const onUp = () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onUp);
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onUp);
  }, []);

  if (state === null || position === null) return null;
  if (typeof document === 'undefined') return null;

  // When the user has dragged the panel, render at their pinned coords;
  // otherwise use the auto-anchored position. `maxWidth` always comes from
  // the auto-position so the panel doesn't visibly "snap wide" when grabbed
  // — the auto recompute keeps producing a sane max for the current viewport.
  const renderTop = userPosition?.top ?? position.top;
  const renderLeft = userPosition?.left ?? position.left;

  return createPortal(
    <div
      ref={overlayRef}
      // `fixed` so we live in the window's coordinate space and aren't
      // clipped by the editor's `overflow-hidden` wrapper.
      className="fixed z-50 pointer-events-auto"
      style={{
        top: renderTop,
        left: renderLeft,
        maxWidth: position.maxWidth,
        minWidth: PANEL_MIN_WIDTH,
      }}
      data-testid="peek-results-overlay"
      data-peek-pinned={userPosition !== null ? 'true' : 'false'}
    >
      <PeekResultsPanel
        state={state}
        onClose={onClose}
        onOpenInResults={onOpenInResults}
        onHeaderPointerDown={handleHeaderPointerDown}
      />
    </div>,
    document.body,
  );
}
